import React, { useEffect, useState, useRef } from 'react';
import {
  StepContainer,
  StepLabelContainer,
  StepPrimaryLabel,
  StepIcon,
  Step,
  StepConnector,
  StepProgressBar,
  StepWrapper,
  MultiToasts,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
} from 'cdk-radial';
import './OrderAPIStepper.scss';
import SelectAPIFields from './SelectAPIFieldsStep/SelectAPIFields';
import ReviewAndRemoveAPIs from './ReviewAPIsStep/ReviewAndRemoveAPIs';
import PricingPlanContainer from './SelectPricing/PricingPlanContainer';
import ReviewTerms from './ReviewTerms/ReviewTerms';
import { useSelector, useDispatch } from 'react-redux';
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES,
} from '../../../../constants/amplitudeConstants';
import { sendAmplitudeData } from '../../../../utils/amplitude';
import { API_TYPES } from '../../../../constants/APIDirectoryConstants';
import { Constants, RESOURCE_TYPES } from '../../../../constants/constants';
import { getMatchingResources } from '../../../../utils/utils'
import { updateCart } from '../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import ConfigureCMFNumber from './ConfigureCMFStep/ConfigureCMFNumber';
import { trackPageView, PAGE_SECTIONS, PAGE_SUBSECTIONS, trackCheckoutError, CHECKOUT_ERROR_CODES, CHECKOUT_ERROR_KEYS, CHECKOUT_ERROR_MESSAGES } from '../../../../utils/gtm';

//Checks if none of the ApiList item is async-api and none has vertical_scoping enabled.
function checkApiList(apiList) {
  for (const apiItem of apiList) {
    if (
      apiItem.apiType === API_TYPES.ASYNC ||
      apiItem.details?.verticalScoping
    ) {
      return true;
    }
  }
  return false;
}

const OrderAPIStepper = () => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(1);
  const [pricedBundleExists, setPricedBundleExists] = useState(false);
  const hasTrackedInitialPageView = useRef(false);
  const prevErrorStates = useRef({});
  const state = useSelector((state) => state);
  const cart = state?.orderAPISlice?.cart;

  // Error states from Redux
  const {
    errorFetchingCartDetails,
    errorDeletingApi,
    errorAddingPricingPlanToApiInCart,
    errorCreatingOrder,
    errorUpdatingOrder,
  } = state?.orderAPISlice || {};
  const [steps, setSteps] = useState([
    {
      label: Constants.API_REVIEW_CART,
    },
    {
      label: Constants.TERMS_AND_CONDITIONS,
    },
  ]);
  const [cartWithAPIdetails, setCartWithAPIdetails] = useState([]);

  useEffect(() => {
      setSteps([
        {
          label: Constants.API_REVIEW_CART,
        },
        ...(pricedBundleExists
              ? [
                  {
                    label: Constants.CONFIGURE_CMF_NUMBER,
                  },
                ]
              : []),
        {
          label: Constants.TERMS_AND_CONDITIONS,
        },
      ]);
  }, [pricedBundleExists, cartWithAPIdetails]);

  useEffect(() => {
    const myCart = [];
    const obsoleteApis = [];
    cart.forEach((api) => {
      let matchingApi = getMatchingResources(
        api,
        state?.apiDirectorySlice?.apisObject,
        state?.apiDirectorySlice?.asyncApisObject,
        state?.apiDirectorySlice?.bundlesObject
      );

      if (matchingApi?.visibility === 'public' || matchingApi?.sharedWithOrg) {
        myCart.push({ ...api, details: matchingApi });
      } else {
        obsoleteApis.push(api);
      }
    });
    if (cart.length !== myCart.length) {
      dispatch(updateCart({ cart: myCart, obsoleteApis }));
    }
    setCartWithAPIdetails(myCart);
  }, [cart]);

  useEffect(() => {
    // Check for priced bundle with installFee whenever cart details change
    // Use cartWithAPIdetails to have access to the full details including pricingPlans
    if (!cartWithAPIdetails.length) {
      setPricedBundleExists(false);
      return;
    }
    
    const doesPricedBUndleExists = cartWithAPIdetails.find((item) => {
      if (item.resourceType?.toLowerCase() === RESOURCE_TYPES.BUNDLE && item.resourcePricingPlanId) {
        // Check if the pricing plan has installFee attribute
        const pricingPlan = item.details?.plans?.find(
          plan => plan.id === item.resourcePricingPlanId
        );
        return pricingPlan?.installFee != null ? true : false;
      }
      return false;
    });
    
    setPricedBundleExists(doesPricedBUndleExists);
  }, [cartWithAPIdetails, cart]);

  useEffect(() => {
    const currentStepObj = steps[activeStep - 1];
    if (!currentStepObj) return;

    let subsection = PAGE_SUBSECTIONS.REVIEW_CART;
    if (currentStepObj.label === Constants.CONFIGURE_CMF_NUMBER) {
      subsection = PAGE_SUBSECTIONS.CONFIGURE_CMF;
    } else if (currentStepObj.label === Constants.TERMS_AND_CONDITIONS) {
      subsection = PAGE_SUBSECTIONS.TERMS_AND_CONDITIONS;
    }

    trackPageView({
      page_section: PAGE_SECTIONS.CHECKOUT,
      page_subsection: subsection,
    });
  }, [activeStep, steps]);

  // Error tracking: detect error state transitions (false → true)
  useEffect(() => {
   
    const errorConfigs = [
      {
        key: CHECKOUT_ERROR_KEYS.ERROR_FETCHING_CART_DETAILS,
        current: errorFetchingCartDetails,
        code: CHECKOUT_ERROR_CODES.FETCH_CART_FAILED,
        message: CHECKOUT_ERROR_MESSAGES.FETCH_CART_FAILED,
        step: Constants.API_REVIEW_CART,
      },
      {
        key: CHECKOUT_ERROR_KEYS.ERROR_DELETING_API,
        current: errorDeletingApi,
        code: CHECKOUT_ERROR_CODES.DELETE_API_FAILED,
        message: CHECKOUT_ERROR_MESSAGES.DELETE_API_FAILED,
        step: Constants.API_REVIEW_CART,
      },
      {
        key: CHECKOUT_ERROR_KEYS.ERROR_ADDING_PRICING_PLAN,
        current: errorAddingPricingPlanToApiInCart,
        code: CHECKOUT_ERROR_CODES.UPDATE_PRICING_PLAN_FAILED,
        message: CHECKOUT_ERROR_MESSAGES.UPDATE_PRICING_PLAN_FAILED,
        step: Constants.API_REVIEW_CART,
      },
      {
        key: CHECKOUT_ERROR_KEYS.ERROR_CREATING_ORDER,
        current: errorCreatingOrder,
        code: CHECKOUT_ERROR_CODES.CREATE_ORDER_FAILED,
        message: CHECKOUT_ERROR_MESSAGES.CREATE_ORDER_FAILED,
        step: Constants.TERMS_AND_CONDITIONS,
      },
      {
        key: CHECKOUT_ERROR_KEYS.ERROR_UPDATING_ORDER,
        current: errorUpdatingOrder,
        code: CHECKOUT_ERROR_CODES.UPDATE_ORDER_FAILED,
        message: CHECKOUT_ERROR_MESSAGES.UPDATE_ORDER_FAILED,
        step: Constants.TERMS_AND_CONDITIONS,
      },
    ];

    errorConfigs.forEach(({ key, current, code, message, step }) => {
      const prev = prevErrorStates.current[key];
      // Track only on transition from false/undefined to true
      if (current && !prev) {
        trackCheckoutError({
          errorCode: code,
          errorMessage: message,
          step: step,
        });
      }
    });

    // Update previous error states
    prevErrorStates.current = {
      errorFetchingCartDetails,
      errorDeletingApi,
      errorAddingPricingPlanToApiInCart,
      errorCreatingOrder,
      errorUpdatingOrder,
    };
  }, [
    errorFetchingCartDetails,
    errorDeletingApi,
    errorAddingPricingPlanToApiInCart,
    errorCreatingOrder,
    errorUpdatingOrder,
    activeStep,
    steps,
  ]);

  const clickStepper = (index) => {
    setActiveStep(index);
  };
  return (
    <div className="orderapi-stepper-conatiner">
      <StepContainer data-testid="stepper">
        <StepWrapper>
          {steps.map((step, i) => {
            const index = i + 1;
            const isLastStep = index === steps.length;
            return (
              <React.Fragment key={index}>
                <Step
                  isActive={activeStep === index}
                  isComplete={activeStep > index}
                  onClick={() => clickStepper(index)}
                  aria-labelledby={`label${index}`}
                  aria-disabled={activeStep < index}
                  aria-controls={`step-content-${index}`}
                  data-testid={`step-button-${index}`}
                >
                  <StepIcon
                    dataTestId={`step-${index}-icon`}
                    stepNumber={index}
                  />
                  <StepLabelContainer>
                    <StepPrimaryLabel
                      data-testid={`step-${index}-primary-label`}
                      id={`label${index}`}
                    >
                      <span className="stepper-label">{step.label}</span>
                    </StepPrimaryLabel>
                  </StepLabelContainer>
                </Step>
                {!isLastStep && <StepConnector aria-hidden="true" />}
              </React.Fragment>
            );
          })}
        </StepWrapper>
        <StepProgressBar totalSteps={steps.length} currentStep={activeStep} />
      </StepContainer>

      {steps[activeStep - 1].label === Constants.API_REVIEW_CART && (
        <ReviewAndRemoveAPIs
          setActiveStep={setActiveStep}
          activeStep={activeStep}
          cart={cartWithAPIdetails}
        />
      )}
      {steps[activeStep - 1].label === Constants.CONFIGURE_CMF_NUMBER && (
        <ConfigureCMFNumber
          setActiveStep={setActiveStep}
          activeStep={activeStep}
          cart={cartWithAPIdetails}
        />
      )}
      {steps[activeStep - 1].label === Constants.TERMS_AND_CONDITIONS && (
        <ReviewTerms
          setActiveStep={setActiveStep}
          activeStep={activeStep}
          cart={cartWithAPIdetails}
        />
      )}
    </div>
  );
};

export default OrderAPIStepper;
